// Bostagi public landing page — behaviour.
//
// Loaded ONLY by layouts/landing.html.erb. Plain vanilla JS (no Vue), so it
// stays out of the dashboard bundle entirely.
//
// Behaviours:
//   1. Signed-in visitors are bounced to /app.
//   2. Analytics (shared Amplitude token) — landing view + CTA clicks.
//   3. Mobile header menu toggle.
//   4. FAQ accordion (keyboard-accessible native buttons).
//   5. Sections reveal on scroll (IntersectionObserver).
//   6. Stat count-up with Arabic-Indic numerals.
//   7. Gentle hero parallax on the inbox mockup (pointer/scroll, desktop only).
// All motion respects prefers-reduced-motion.

import * as amplitude from '@amplitude/analytics-browser';
import '../landing/landing.scss';

const prefersReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
).matches;

// --- 1. Bounce signed-in users straight to the app -------------------------
// Dashboard auth is a client-side cookie (see dashboard/store/utils/api.js),
// so this can only be decided in the browser.
function redirectIfSignedIn() {
  if (document.body.dataset.page !== 'landing') return false;
  if (/(?:^|;\s*)cw_d_session_info=/.test(document.cookie)) {
    window.location.replace('/app');
    return true;
  }
  return false;
}

// --- 2. Analytics (reuses the shared Amplitude token, like the dashboard) ---
function setupAnalytics() {
  const token = window.analyticsConfig && window.analyticsConfig.token;
  if (!token) return;
  // Respect Do Not Track — this is a public marketing page.
  if (navigator.doNotTrack === '1' || window.doNotTrack === '1') return;

  amplitude.init(token, { defaultTracking: false });
  amplitude.track('landing_view');

  document.addEventListener('click', e => {
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const location = (a.closest('section, header, footer') || {}).id || 'page';

    if (href.includes('/app/auth/signup')) {
      amplitude.track('landing_cta_click', {
        cta: a.dataset.bostagiCta || 'signup',
        location,
      });
    } else if (href.includes('/app/login')) {
      amplitude.track('landing_login_click', { location });
    } else if (a.dataset.bostagiCta === 'demo') {
      amplitude.track('landing_cta_click', { cta: 'demo', location });
    }
  });
}

// --- 3. Mobile header menu -------------------------------------------------
function setupMenu() {
  const btn = document.querySelector('[data-menu-btn]');
  const menu = document.querySelector('[data-menu]');
  if (!btn || !menu) return;

  const setOpen = open => {
    menu.classList.toggle('open', open);
    btn.setAttribute('aria-expanded', String(open));
  };

  btn.addEventListener('click', () =>
    setOpen(btn.getAttribute('aria-expanded') !== 'true')
  );
  menu.addEventListener('click', e => {
    if (e.target.closest('a')) setOpen(false);
  });
}

// --- 4. FAQ accordion ------------------------------------------------------
function setupFaq() {
  document.querySelectorAll('.faq-q').forEach(q => {
    q.addEventListener('click', () => {
      const expanded = q.getAttribute('aria-expanded') === 'true';
      const panel = document.getElementById(q.getAttribute('aria-controls'));
      q.setAttribute('aria-expanded', String(!expanded));
      if (panel) {
        panel.style.maxHeight = expanded ? null : `${panel.scrollHeight}px`;
      }
    });
  });
}

// --- 5. Reveal on scroll ---------------------------------------------------
function setupReveal() {
  const els = Array.from(document.querySelectorAll('.reveal'));
  if (!els.length) return;

  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    els.forEach(el => el.classList.add('in'));
    return;
  }

  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  els.forEach(el => io.observe(el));

  // Fail-safe: never leave content hidden.
  setTimeout(() => els.forEach(el => el.classList.add('in')), 6000);
}

// --- 6. Stat count-up (Arabic-Indic numerals) ------------------------------
const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
function toArabicDigits(n) {
  return String(n)
    .split('')
    .map(c => (/[0-9]/.test(c) ? AR_DIGITS[+c] : c))
    .join('');
}
function animateCount(el) {
  const target = Number(el.getAttribute('data-count'));
  const suffix = el.getAttribute('data-suffix') || '';
  if (prefersReducedMotion) {
    el.textContent = toArabicDigits(target) + suffix;
    return;
  }
  const duration = 1400;
  let start = null;
  const step = ts => {
    if (!start) start = ts;
    const p = Math.min((ts - start) / duration, 1);
    const eased = 1 - (1 - p) ** 3;
    el.textContent = toArabicDigits(Math.round(target * eased)) + suffix;
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
function setupCounters() {
  const counters = Array.from(document.querySelectorAll('[data-count]'));
  if (!counters.length) return;
  if (!('IntersectionObserver' in window)) {
    counters.forEach(animateCount);
    return;
  }
  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.6 }
  );
  counters.forEach(c => io.observe(c));
}

// --- 7b. Lead form (landing contact section) -------------------------------
function setupLeadForm() {
  const form = document.getElementById('leadForm');
  if (!form) return;

  const status = form.querySelector('.lead-status');
  const submitBtn = form.querySelector('button[type="submit"]');
  const csrfToken = document.querySelector('meta[name="csrf-token"]');

  form.addEventListener('submit', async e => {
    e.preventDefault();
    status.textContent = '';
    status.classList.remove('success', 'error');
    submitBtn.disabled = true;

    const lead = {
      name: form.name.value,
      email: form.email.value,
      phone: form.phone.value,
      description: form.description.value,
    };

    try {
      const response = await fetch('/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken.content }),
        },
        body: JSON.stringify({ lead }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        status.textContent = 'تم استلام طلبك! هنكلّمك قريب جدًا.';
        status.classList.add('success');
        form.reset();
        // Marketing pixel conversions; fbq/oaiq/ttq only exist when the
        // matching pixel is configured.
        if (window.fbq) window.fbq('track', 'Lead');
        if (window.oaiq) window.oaiq('track', 'Lead');
        if (window.ttq) window.ttq.track('SubmitForm');
      } else {
        status.textContent =
          (data.errors && data.errors.join('، ')) ||
          'حصل خطأ، جرّب تاني أو كلّمنا مباشرة.';
        status.classList.add('error');
      }
    } catch {
      status.textContent = 'حصل خطأ في الاتصال، جرّب تاني أو كلّمنا مباشرة.';
      status.classList.add('error');
    } finally {
      submitBtn.disabled = false;
    }
  });
}

// --- 7. Gentle hero parallax ----------------------------------------------
function setupParallax() {
  if (prefersReducedMotion) return;
  const frame = document.querySelector('.mock-frame');
  if (!frame || !window.matchMedia('(min-width:980px)').matches) return;
  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      if (y < 700) frame.style.transform = `translateY(${y * -0.02}px)`;
    },
    { passive: true }
  );
}

function init() {
  if (redirectIfSignedIn()) return;
  setupAnalytics();
  setupMenu();
  setupFaq();
  setupLeadForm();
  setupReveal();
  setupCounters();
  setupParallax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
